/**
 * Reading ideas out of typed text, shared by the New ideas form
 * (`pages/NewIdeaBatch.tsx`) and the board's quick add (`board/QuickAdd.tsx`),
 * so both send the server the same thing for the same text.
 */

/** The lines the server will keep: trimmed, blank ones dropped, repeats removed. */
export function ideaLines(text: string): string[] {
  return [...new Set(text.split('\n').map((line) => line.trim()).filter((line) => line !== ''))];
}

function cellsOf(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isSeparatorRow(line: string): boolean {
  if (!line.includes('|')) return false;
  const cells = cellsOf(line).map((cell) => cell.replace(/\s/g, ''));
  return cells.length > 0 && cells.every((cell) => /^:?-{2,}:?$/.test(cell));
}

/**
 * Whether the text is a pasted planning table rather than one idea per line:
 * some line is a `|---|---|` separator row, as the server reads it.
 */
export function looksLikeTable(text: string): boolean {
  return text.split('\n').some(isSeparatorRow);
}

/**
 * The body rows of a pasted table, each as its first filled-in cell: the
 * rows under the first separator that have a `|` in them. Only a label; the
 * server reads the table itself.
 */
export function tableRowLabels(text: string): string[] {
  const lines = text.split('\n');
  const separator = lines.findIndex(isSeparatorRow);
  if (separator === -1) return [];
  return lines
    .slice(separator + 1)
    .filter((line) => line.includes('|') && !isSeparatorRow(line))
    .map((line) => cellsOf(line).find((cell) => cell !== '') ?? '')
    .filter((label) => label !== '');
}
